import React, { useState, useEffect, lazy, Suspense } from 'react';
import { PropTypes } from 'prop-types';
import { useParams } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import LoadingWrapper from 'components/Common/LoadingWrapper';
import FormGroupTypes from 'common/forms/formGroupTypes';
import { submissionStages } from 'common/forms/submissionStages';
import SupportingDocumentsModal from 'components/Modals/SupportingDocumentsModal';
import FormConfirmation from 'components/Modals/FormConfirmation';
import PenaltiesOfPerjuryModal from 'components/Modals/PenaltiesOfPerjuryModal';
import FormSigning from 'components/Modals/FormSigning';

import {
  setSubmissionStage,
  submitFormGroup,
  confirmFormGroup,
  getFormGroupFormPreviewPdf,
  certifyFormGroup,
  getFormGroupSupportingDocs,
  clearPdfDownload,
  getCollectableSupportingDocPdf,
} from 'actions';

const FormQuestionnaire = lazy(() => import(/* webpackChunkName: "blank.questionnaires" */ 'components/Questionnaires/FormQuestionnaire'));

const FormGroupContainer = ({
  supportingDocs,
  dispatch,
  workflowState,
  submissionStage,
  intl,
  pdfData,
  isFetchingPdfForm,
  downloadRequestFormType,
  englishMessages,
  isFetchingCollectablePdf,
  collectableSupportingDocPdfData,
  selectedAccountId,
  questionnaire,
  validationResult }) => {
  const [showSupportingDocs, setShowSupportingDocs] = useState(false);
  const [downloadFormType, setFormType] = useState('');
  const [downloadCollectableSupportingDocType, setCollectableSupportingType] = useState('');
  const { groupId, formGroupType } = useParams();

  const signAndSubmit = () => {
    if (workflowState && workflowState.stage === 2) {
      dispatch(setSubmissionStage(submissionStages.Signing));
      return;
    }

    dispatch(submitFormGroup(groupId, submissionStages.Confirmation));
  };

  const onFormConfirmationConfirm = () => {
    dispatch(confirmFormGroup(formGroupType, groupId));
  };

  const resetSubmisson = () => {
    dispatch(setSubmissionStage(submissionStages.Validating));
  };

  const onFormConfirmationCancel = () => {
    resetSubmisson();
  };

  const downloadPreviewPdf = (formType) => {
    setFormType(formType);
    dispatch(getFormGroupFormPreviewPdf(groupId, formType, intl));
  };

  const downloadCollectableSupportingDocPreviewPdf = (collectableSupportingDocType) => {
    setCollectableSupportingType(collectableSupportingDocType);
    dispatch(getCollectableSupportingDocPdf(
      {
        UploadedFormId: groupId,
        accountId: selectedAccountId,
        customerName: `${ questionnaire?.answers?.name?.firstName } ${ questionnaire?.answers?.name?.lastName }`,
        printName: questionnaire?.answers?.signer?.name,
        customerId: validationResult.customerId,
        collectableSupportingDocType,
        signature: null
      },
      intl
    ));
  };

  const onCertificationConfirm = () => {
    dispatch(certifyFormGroup(groupId));
  };

  const onCertificationCancel = () => {
    resetSubmisson();
  };

  const onFormSigningCancel = () => {
    resetSubmisson();
  };

  useEffect(() => {
    if (!isFetchingPdfForm && pdfData) {
      const link = document.createElement('a');
      link.href = pdfData;
      link.setAttribute('target', '_blank');
      link.setAttribute('download', `${ downloadFormType }.pdf`);
      document.body.appendChild(link);
      link.click();
    }
  }, [pdfData]);

  useEffect(() => {
    if (!isFetchingCollectablePdf && collectableSupportingDocPdfData) {
      const link = document.createElement('a');
      link.href = collectableSupportingDocPdfData;
      link.setAttribute('target', '_blank');
      link.setAttribute('download', `${ downloadCollectableSupportingDocType }.pdf`);
      document.body.appendChild(link);
      link.click();
    }
  }, [collectableSupportingDocPdfData]);

  useEffect(() => {
    dispatch(getFormGroupSupportingDocs(groupId));

    return () => {
      dispatch(clearPdfDownload());
    };
  }, []);

  return (
    <React.Fragment>
      { groupId && formGroupType && <Suspense fallback={ <div className="loading-container"><LoadingWrapper text={ intl.formatMessage({ id: 'pleaseWaitText' }) } /></div> }>
        <FormQuestionnaire
          formGroupId={ groupId }
          formGroupType={ formGroupType }
          onSignSubmit={ () => signAndSubmit() }
          showSupportingDocs={ () => setShowSupportingDocs(true) }
        />
      </Suspense> }
      { submissionStage === submissionStages.Confirmation && <FormConfirmation
        onConfirm={ () => onFormConfirmationConfirm() }
        onCancel={ () => onFormConfirmationCancel() }
        intl={ intl }
        previewOnlyMode={ false }
        onClickHandler={ formType => downloadPreviewPdf(formType) }
        isFetchingPdf={ isFetchingPdfForm }
        downloadFormType={ downloadRequestFormType }
        formGroup={ FormGroupTypes.find(group => group.key === formGroupType) }
        supportingDocOnClickHandler={ supportingDocType => downloadCollectableSupportingDocPreviewPdf(supportingDocType) }
        isFetchingCollectablePdf={ isFetchingCollectablePdf }
      /> }
      { showSupportingDocs && <SupportingDocumentsModal
        id={ groupId }
        supportingDocs={ supportingDocs }
        onCancel={ () => setShowSupportingDocs(false) }
        locale="en"
        isFormGroup
      /> }
      { submissionStage === submissionStages.Certification && <PenaltiesOfPerjuryModal
        formGroup={ FormGroupTypes.find(group => group.key === formGroupType) }
        onConfirm={ () => onCertificationConfirm() }
        onCancel={ () => onCertificationCancel() }
      /> }
      { submissionStage === submissionStages.Signing && <FormSigning
        formGroup={ FormGroupTypes.find(group => group.key === formGroupType) }
        formGroupId={ groupId }
        onCancel={ () => onFormSigningCancel() }
      /> }
    </React.Fragment>
  );
};

FormGroupContainer.propTypes = {
  supportingDocs: PropTypes.arrayOf(PropTypes.shape({})),
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.shape({
    path: PropTypes.string,
    params: PropTypes.shape({
      formGroupType: PropTypes.string,
      groupId: PropTypes.string
    })
  }).isRequired,
  workflowState: PropTypes.shape({
    stage: PropTypes.number
  }),
  submissionStage: PropTypes.string,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired,
  pdfData: PropTypes.string,
  isFetchingPdfForm: PropTypes.bool,
  downloadRequestFormType: PropTypes.string,
  englishMessages: PropTypes.shape({}).isRequired,
  isFetchingCollectablePdf: PropTypes.bool,
  collectableSupportingDocPdfData: PropTypes.string,
  selectedAccountId: PropTypes.number,
  questionnaire: PropTypes.shape({
    answers: PropTypes.shape({
      name: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string
      }),
      signer: PropTypes.shape({
        name: PropTypes.string
      })
    })
  }),
  validationResult: PropTypes.shape({
    customerId: PropTypes.string,
    customerName: PropTypes.string,
  })
};

FormGroupContainer.defaultProps = {
  supportingDocs: [],
  workflowState: null,
  submissionStage: submissionStages.Validating,
  pdfData: null,
  isFetchingPdfForm: false,
  downloadRequestFormType: null,
  isFetchingCollectablePdf: false,
  collectableSupportingDocPdfData: null,
  selectedAccountId: null,
  questionnaire: null,
  validationResult: null,
};

function mapStateToProps(state, ownProps) {
  const {
    app: { englishMessages },
    form: {
      supportingDocs,
      workflowState,
      submissionStage,
      pdfData,
      isFetchingPdfForm,
      downloadRequestFormType,
      selectedAccountId,
      validationResult
    },
    supportingDocuments: {
      isFetchingCollectablePdf,
      collectableSupportingDocPdfData
    },
    questionnaires: {
      formQuestionnaire
    }
  } = state;

  return {
    workflowState,
    supportingDocs,
    submissionStage,
    pdfData,
    isFetchingPdfForm,
    downloadRequestFormType,
    englishMessages,
    isFetchingCollectablePdf,
    collectableSupportingDocPdfData,
    selectedAccountId,
    questionnaire: formQuestionnaire,
    validationResult
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(FormGroupContainer));
